import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PiModal from './PiModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Pn {
  pi_id: number;
  pi_name: string;
  pi_status: number;
  }

const Pn: React.FC = () => {
  const [pis, setPis] = useState<Pn[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Pn | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchPn = async () => {
    try {
      const data = await apiRequest<Pn[]>({
        method: 'get',
        url: 'product-info',
      });
      setPis(data);
    } catch (error) {
      showError('Failed to fetch Product Info');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPn();
  }, []);

  const handleEditClick = (record: Pn) => {
    setIsEditMode(true);
    setInitialValues({
      pi_id: record.pi_id,
      pi_name: record.pi_name,
      pi_status: record.pi_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchPn();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pi_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-product-info/${pi_id}`,
      });
      showSuccess('Product Info deleted');
      fetchPn();
    } catch {
      showError('Failed to delete Product Info');
    }
  };

  const handleStatusToggle = async (qnId: number) => {
    setPis((prev) =>
      prev.map((item) =>
        item.pi_id === qnId
          ? { ...item, pi_status: item.pi_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-product-info/${qnId}`,
      });
      showSuccess('Product Info status updated');
    } catch (error) {
      showError('Failed to update status');
      setPis((prev) =>
        prev.map((item) =>
          item.pi_id === qnId
            ? { ...item, pi_status: item.pi_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Pn>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Pn> = [
    {
      title: 'Product Info',
      dataIndex: 'pi_name',
      key: 'pi_name',
      filters: Array.from(new Set(pis.map((s) => s.pi_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pi_name || null,
      onFilter: (value, record) => record.pi_name.includes(value as string),
      sorter: (a, b) => a.pi_name.localeCompare(b.pi_name),
      sortOrder: sortedInfo.columnKey === 'pi_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pi_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Pn) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pi_status}
                onChange={() => handleStatusToggle(record.pi_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Product Info?"
              onConfirm={() => handleDelete(record.pi_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Product Info</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Product Info
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Pn>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pi_id"
          dataSource={pis.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No Product Info found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, pis.length)} of{' '}
                {pis.length}
              </span>
              <Pagination
                {...pagination}
                total={pis.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PiModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Pn;